/**
 * 收银台监听工具 - 观察器
 * @author Nemo
 * @date 2026-01-26
 */

import { findElement, isElementRendered, retryFind, reportError } from './helpers';

/**
 * 元素观察器基类
 */
export class ElementObserver {
  constructor(options) {
    this.name = options.name; // 观察器名称
    this.vm = options.vm; // Vue实例
    this.ref = options.ref; // ref名称
    this.selector = options.selector; // CSS选择器
    this.onReady = options.onReady; // 就绪回调
    this.maxRetry = options.maxRetry || 3;
    this.retryDelay = options.retryDelay || 500;
    this.cancelFn = null; // 取消函数
  }

  /**
   * 启动观察
   */
  start() {
    // 清理旧的观察
    this.stop();

    // 启动新的观察，保存取消函数
    this.cancelFn = retryFind(
      () => this.findElement(),
      {
        maxRetry: this.maxRetry,
        delay: this.retryDelay,
        vm: this.vm, // 传递vm用于错误上报
        onComplete: (element, success) => {
          this.onReady?.(element, success); // 成功和失败都回调
        },
      },
    );
  }

  /**
   * 查找元素
   */
  findElement() {
    return findElement({
      vm: this.vm,
      ref: this.ref,
      selector: this.selector,
    });
  }

  /**
   * 停止观察
   */
  stop() {
    if (this.cancelFn) {
      this.cancelFn();
      this.cancelFn = null;
    }
  }
}

/**
 * 订单观察器
 */
export class OrderObserver {
  constructor(options) {
    this.vm = options.vm;
    this.onReady = options.onReady;
  }

  start() {
    try {
      // 判断订单数据是否存在（在initOrder/fetchOrder之后调用）
      const success = !!(this.vm && this.vm.order && this.vm.payMethods);

      this.onReady?.(
        success ? {
          order: this.vm.order,
          payMethods: this.vm.payMethods,
        } : null,
        success, // true = 有数据，false = 没数据
      );
    } catch (error) {
      // 捕获异常，上报错误
      reportError(this.vm, error, 'order_observer');
      this.onReady?.(null, false);
    }
  }

  stop() {
    // 无需清理
  }
}

/**
 * 支付按钮观察器（处理多按钮场景）
 */
export class SubmitButtonObserver extends ElementObserver {
  constructor(options) {
    super({
      ...options,
      name: '提交按钮',
      ref: 'submitBtn',
      selector: '.btn-submit', // 移动端使用selector
    });
  }

  /**
   * 查找所有提交按钮（可能有多个）
   */
  findElement() {
    try {
      let foundElement = null;

      // 优先使用ref（PC端）
      const ref = this.vm?.$refs?.submitBtn;

      if (ref) {
        // 如果是数组，找到第一个已渲染的
        if (Array.isArray(ref)) {
          for (const btn of ref) {
            if (!btn) continue;
            const element = btn.$el || btn;
            if (isElementRendered(element)) {
              foundElement = element;
              break;
            }
          }
        } else {
          // 单个按钮
          const element = ref.$el || ref;
          if (isElementRendered(element)) {
            foundElement = element;
          }
        }
      }

      // 如果ref没找到可见按钮，使用selector查找（兼容Token支付等场景）
      if (!foundElement && this.selector) {
        const allButtons = document.querySelectorAll(this.selector);
        for (const btn of allButtons) {
          if (isElementRendered(btn)) {
            foundElement = btn;
            break;
          }
        }
      }

      return foundElement;
    } catch (error) {
      // 捕获异常，上报错误
      reportError(this.vm, error, 'submit_button_find');
      return null;
    }
  }
}
